<template lang="pug">
.sent-emails-list
    el-card.contains-table(shadow="never")
        paginated-items(
            scope="ex"
            type="nwsltr_sent_email"
            :loading="loading"
            :items="items"
            :filters="filters"
            :show-filters="showFilters"
            :variables="variables"
            :all-columns="allColumns"
            :active-columns="activeColumns"
            :page-info="pageInfo"
            :sort="sort"
            @pagination-size-change="handleSizeChange"
            @pagination-current-change="handleCurrentChange"
            @show-table-columns="handleColsShow"
            @change-filter="handleFilterInputChange"
            @change-sort="handleSortInputChange"
        )
            template(slot="top")
                el-row.table-top(type="flex" justify="space-between")
                    .row
                        .left
                            i.el-icon-fa-envelope.far.mr-2
                            span.font-semibold {{ $t('Sent Emails') }}
                        .right.hide-on-empty
                            el-button(
                                plain
                                size="mini"
                                type="primary"
                                icon="el-icon-fa-filter fas"
                                @click="toggleFilterVisibility"
                                :disabled="hasFilters"
                            )
                                span(v-if="showFilters") {{ $t('Hide filters') }}
                                span(v-else) {{ $t('Show filters') }}

            //- Status column with tag styling
            template(v-slot:status="{ value }")
                el-tag(:type="getStatusType(value)" size="mini") {{ $t(value) }}

            //- Actions column with eye icon
            template(v-slot:actions="{ item }")
                el-button(
                    circle
                    plain
                    size="mini"
                    type="primary"
                    icon="el-icon-fa-eye fas"
                    @click.stop="previewEmail(item.id)"
                )
</template>

<i18n>
{
    "en": {
        "delivered": "Delivered",
        "bounced_permanent": "Hard Bounce",
        "bounced_temporary": "Soft Bounce",
        "complained": "Complained",
        "pending": "Pending"
    },
    "de": {
        "Sent Emails": "Gesendete E-Mails",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "delivered": "Zugestellt",
        "bounced_permanent": "Hard Bounce",
        "bounced_temporary": "Soft Bounce",
        "complained": "Beschwert",
        "pending": "Ausstehend"
    }
}
</i18n>

<script>
import _ from 'lodash'
import { adminClient as apolloClient } from '@/store/apollo'
import pinia from '@/pinia'
import { useSetupStore, useNewsletterNewsletterStore } from '@/stores'

import PaginatedItems from '@/components/PaginatedItems'

export default {
    name: 'SentEmailsList',
    components: {
        PaginatedItems
    },
    props: {
        newsletterId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {}
    },
    computed: {
        newsletterStore() {
            return useNewsletterNewsletterStore()
        },
        sentEmailsPaginated() {
            return this.newsletterStore.sentEmailsPaginated
        },
        loading() {
            return _.get(this.sentEmailsPaginated, 'loading', false)
        },
        items() {
            return _.get(this.sentEmailsPaginated, 'items', [])
        },
        pageInfo() {
            return _.get(this.sentEmailsPaginated, 'pageInfo', {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            })
        },
        allColumns() {
            return _.get(this.sentEmailsPaginated, 'allColumns', [])
        },
        activeColumns() {
            return _.get(this.sentEmailsPaginated, 'activeColumns', [])
        },
        variables() {
            return _.get(this.sentEmailsPaginated, 'variables', {
                order: 'created_at',
                direction: 'desc'
            })
        },
        showFilters() {
            return this.hasFilters || _.get(this.sentEmailsPaginated, 'showFilters', false)
        },
        hasFilters() {
            return !_.isEmpty(this.filters)
        },
        filters() {
            return _.cloneDeep(_.get(this.sentEmailsPaginated, 'filters', {}))
        },
        sort() {
            var variables = this.variables
            var defaultSort = _.get(this.sentEmailsPaginated, 'defaultSort') || { field: 'created_at', direction: 'desc' }
            var order = (variables.order !== undefined && variables.order !== '') ? variables.order : defaultSort.field
            var direction = (variables.direction !== undefined && variables.direction !== '') ? variables.direction : defaultSort.direction
            return { field: order, direction: direction }
        },
        setupStore() {
            return useSetupStore();
        }
    },
    watch: {
        newsletterId: {
            immediate: true,
            handler() {
                this.initialize()
            }
        }
    },
    methods: {
        saveFilterVisibility(payload) {
            return this.setupStore.saveFilterVisibility(payload);
        },
        fetchSentEmailsPaginated(payload) {
            return this.newsletterStore.fetchSentEmailsPaginated(payload)
        },
        fetchEmailPreview(emailId) {
            return this.newsletterStore.fetchEmailPreview(emailId)
        },
        setSentEmailsPaginatedLoading(value) {
            this.newsletterStore.setSentEmailsPaginatedLoading(value)
        },
        defineSentEmailsCurrentPage(currentPage) {
            this.newsletterStore.defineSentEmailsCurrentPage(currentPage)
        },
        defineSentEmailsItemsPerPage(perPage) {
            this.newsletterStore.defineSentEmailsItemsPerPage(perPage)
        },
        defineSentEmailsFilterValue(payload) {
            this.newsletterStore.defineSentEmailsFilterValue(payload)
        },
        defineSentEmailsFilterVisibility(value) {
            this.newsletterStore.defineSentEmailsFilterVisibility(value)
        },
        defineSentEmailsActiveColumns(activeColumnKeys) {
            this.newsletterStore.defineSentEmailsActiveColumns(activeColumnKeys)
        },
        defineSentEmailsOrder(payload) {
            this.newsletterStore.defineSentEmailsOrder(payload)
        },
        resetSentEmailsPaginated() {
            this.newsletterStore.resetSentEmailsPaginated()
        },
        initialize() {
            if (this.newsletterId) {
                this.setSentEmailsPaginatedLoading(true)
                this.refetch()
            }
        },
        refetch(options = {}) {
            const silent = options.silent || false
            if (!silent) {
                this.setSentEmailsPaginatedLoading(true)
            }
            return this.fetchSentEmailsPaginated({ newsletterId: this.newsletterId, silent })
                .then(() => {
                    if (!silent) {
                        this.setSentEmailsPaginatedLoading(false)
                    }
                })
                .catch(err => {
                    if (!silent) {
                        this.setSentEmailsPaginatedLoading(false)
                    }
                    console.error(err)
                })
        },
        debouncedRefetch: _.debounce(function() {
            this.setSentEmailsPaginatedLoading(true)
            this.refetch()
        }, useSetupStore(pinia).debounce || 300),
        handleSizeChange(itemsPerPage) {
            this.setSentEmailsPaginatedLoading(true)
            this.defineSentEmailsItemsPerPage(itemsPerPage)
            this.refetch()
        },
        handleCurrentChange(currentPage) {
            this.setSentEmailsPaginatedLoading(true)
            this.defineSentEmailsCurrentPage(currentPage)
            this.refetch()
        },
        handleColsShow(visibleColumns) {
            this.defineSentEmailsActiveColumns(visibleColumns)
            apolloClient.clearStore()
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.defineSentEmailsCurrentPage(1)
            this.defineSentEmailsFilterValue({ key, value })
            if (debounce) this.debouncedRefetch()
            else this.refetch()
        },
        handleSortInputChange(key, value) {
            this.defineSentEmailsOrder({ order: key, direction: value })
            this.refetch()
        },
        toggleFilterVisibility() {
            const visible = this.showFilters
            const value = !visible
            this.defineSentEmailsFilterVisibility(value)
            this.saveFilterVisibility({ scope: 'ex', key: 'nwsltr_sent_email', value })
        },
        previewEmail(emailId) {
            this.fetchEmailPreview(emailId)
        },
        getStatusType(status) {
            const statusTypes = {
                delivered: 'success',
                bounced_permanent: 'danger',
                bounced_temporary: 'warning',
                complained: 'danger',
                pending: 'info'
            }
            return statusTypes[status] || 'info'
        }
    },
    beforeDestroy() {
        this.resetSentEmailsPaginated()
    }
}
</script>

<style lang="sass" scoped>
.sent-emails-list
    margin-top: 20px

.table-top
    .row
        display: flex
        justify-content: space-between
        width: 100%

    .left
        display: flex
        align-items: center

        i
            color: #409eff

    .font-semibold
        font-weight: 600

    .mr-2
        margin-right: 8px
</style>
